<template>
    <div class="modal fade show add-note-modal" aria-labelledby="addNewCardTitle">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                            <h1 class="mb-0">Scheduler</h1>
                            <div>
                              <button v-if="canShowDelete"
                                      :disabled="loading"
                                      class="btn btn-danger btn-sm m-1"
                                      @click="handleMarkDoneSave('inactive')"
                              >
                                <i class="fa fa-trash"></i>
                              </button>

                              <button
                                  @click="closeModal"
                                  type="button"
                                  class="btn-close"
                                  data-bs-dismiss="modal"
                                  aria-label="Close">
                              </button>
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="mt-0 mb-0">

                <div class="px-2">
                    <ul class="nav nav-tabs">
                        <li v-for="tab in allTabs" :key="tab.id" class="nav-item">
                            <a
                                :class="['nav-link', { active: activeTab.id == tab.id }]"
                                @click="setActiveTab(tab)"
                            >
                              {{ tab.title }}
                            </a>
                        </li>
                    </ul>
                </div>

                <div class="col-12">
                    <component
                        :is="availableTabComponents[activeTab.id]"
                        :startDate="startDate"
                        :endDate="endDate"
                        :editData="editData"
                        :taskStatus="taskStatus"
                        :workStatus="workStatus"
                        :markAsDoneShow="markAsDoneShow"
                        :isCompleted="isCompleted"
                        :tagList="tagList"
                        :priorityList="priorityList"
                        :dealData="dealData"
                        :dealId="dealId"
                        :dealCustomer="dealCustomer"
                        :isFromDeal="isFromDeal"
                        @handleSubmit="handleSave"
                        ref="childRef"
                    />
                </div>

                <div class="row justify-content-between">
                    <div class="col-md-6 col-12 ml-2 btn-margin">
                        <button
                            v-if="canShowMarkAsDone"
                            :disabled="loading"
                            class="btn btn-sm btn-success"
                            @click="handleMarkDoneSave(hasReportTo ? 'completed' : 'approved')"
                        >
                            <i class="fa fa-check"></i> Make Done
                        </button>

                        <button
                            v-if="canShowApproveStatus || canShowApprove"
                            class="btn btn-sm btn-success"
                            @click="handleApproved()"
                        >
                          <i :class="canShowApproveStatus ? 'fa fa-check' : 'fa fa-thumbs-up'"></i>
                          {{ canShowApproveStatus ? 'Approved' : 'Approve' }}
                        </button>

                        <button
                            v-if="canShowWorkStatusUpdate"
                            :disabled="loading" class="btn btn-sm btn-info ms-1"
                            @click="handleWorkStatusSave()"
                        >
                          <i class="fa fa-sync"></i> Update Work Status
                        </button>
                    </div>

                    <div class="col-md-4 col-12 ml-2 btn-margin text-end">
                        <button v-if="canShowSave" class="btn btn-primary px-3" :disabled="loading" @click="handleSave">
                           <i class="fa fa-save"></i> Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <WorkStatusTimeSheetModal
        v-if="store.state.isModalOpenTwo"
        :workStatusTimeSheet="workStatusTimeSheet"
        :canShowApproveStatus="canShowApproveStatus"
        :canShowApprove="canShowApprove"
        :loading="loading"
        @handleMarkDoneSave="handleMarkDoneSave"
      />
</template>

<script setup>
import {computed, inject, nextTick, onMounted, ref} from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Task from "./scheduler/Task.vue";
import Event from "./scheduler/Event.vue";
import WorkStatusTimeSheetModal from "./scheduler/WorkStatusTimeSheetModal.vue";
import handleTagRegister from "@/services/modules/scm/tag-register";
import handleActivity from "@/services/modules/procurement/activity";
import handleHospital from '@/services/modules/hospital'
import useDate from '@/services/utils/day'

const props = defineProps({
    startDate: String,
    endDate: String,
    editData: {
      type:Object,
      default: {}
    },
    taskStatus: {
      type:Object,
      default: {}
    },
    workStatus: {
      type:Object,
      default: {}
    },
    markAsDoneShow:{
      type:Boolean,
      default: false
    },
    isCompleted:{
      type:Boolean,
      default: false
    },
    companyRoles: {
        type: Array,
        default: () => []
    },
    dealId: {
      type: Number,
      default: null
    },
    dealCustomer: {
        type: Object,
        required: true
    },
    dealData: {
      type: Object,
      default: {}
    },
    isFromDeal: {
      type: Boolean,
      default: false
    }
})

const emit = defineEmits(['submitted'])
const store = useStore();
const route = useRoute();

const showError = inject('showError');
const showSuccess = inject('showSuccess');

const childRef = ref(null);
const loading = ref(false);
const tagList = ref([]);
const priorityList = ref([])
const workStatusTimeSheet = ref([])

const canShowMarkAsDone = computed(() =>
    props.markAsDoneShow && !props.isCompleted && props.taskStatus.assigned_to
);

const canShowApprove = computed(() =>
    props.markAsDoneShow && props.isCompleted && props.taskStatus.report_to && props.editData.status !== 'approved'
);

const canShowApproveStatus = computed(() =>
    props.markAsDoneShow && props.isCompleted && props.taskStatus.report_to && props.editData.status == 'approved'
);

const canShowDelete = computed(() =>
    !props.isCompleted && props.taskStatus.is_created
);

const canShowWorkStatusUpdate = computed(() =>
    props.workStatus && Object.keys(props.workStatus).length > 0
);

const hasReportTo = computed(() => {
  return  props.editData && props.editData.values.some(value => value.field_key === 'report_to');
});

const canShowSave = computed(() => {
  const ts = props.taskStatus || {};
  const hasPermission =
      ts.report_to || ts.assigned_to || ts.created_by || !ts.participant;
  const isSaveAllowed =
      !props.isCompleted || (props.isCompleted && ts.report_to) || Object.keys(ts).length === 0;

  return isSaveAllowed && hasPermission;
});

const {fetchTagRegisterList} = handleTagRegister()
const {
  storeTaskActivityLog,
  updateTaskActivityLog,
  updateMarkAsDoneTaskActivityLog,
  updateActivityLogWorkStatus,
  fetchTaskActivityWorkStatusLog
} = handleActivity()
const { fetchNoteMasterList } = handleHospital()
const { formatDateTime, formatToDate } = useDate()

const tabs = [
    { id: 'task', title: 'Task', ref: 'taskRef' },
    { id: 'event', title: 'Event', ref: 'eventRef' },
];

const allTabs = computed(() => {
  if (props.editData && props.editData.type) {
    return tabs.filter(tab => tab.id === props.editData.type);
  }
  return tabs;
});

const tabComponents = {
  'task': Task,
  'event': Event
};

const availableTabComponents = computed(() => {
  const result = {};
  allTabs.value.forEach(tab => {
    if (tabComponents[tab.id]) {
      result[tab.id] = tabComponents[tab.id];
    }
  });
  return result;
});

const activeTab = ref(allTabs.value && allTabs.value[0]);

const setActiveTab = (tab) => {
    activeTab.value = tab;
};

const handleApproved = () => {
  const data = childRef.value.getMarkDoneFormData();
  let query = `?company_id=${route.params.companyId}`;

  fetchTaskActivityWorkStatusLog(data.id, query)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        workStatusTimeSheet.value = res.data;

      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
    loading.value = false
  })

  store.state.isModalOpenTwo = true;
}

const handleMarkDoneSave = (status) => {
  if (status === 'inactive' && !window.confirm("Are you sure you want to delete this task?")) {
    return;
  }

  loading.value = true;
  let companyId = route.params.companyId;
  const data = childRef.value.getMarkDoneFormData();

  let formattedData = {
    id: data.id,
    status: status,
    company_id: companyId
  }

  if (!formattedData.id){
    showError("Data No Found")
    return
  }

  updateMarkAsDoneTaskActivityLog(formattedData, data.id)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        emit('submitted')
        closeModal()
      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
    loading.value = false
  })
}

const handleWorkStatusSave = () => {
  loading.value = true;
  let companyId = route.params.companyId;
  const data = childRef.value.getWorkStatusFormData();

  let formattedData = {
    id: data.id,
    area_id: data.area_id ? data.area_id.id : null,
    hours: data.hours,
    date: data.date,
    conveyance: data.conveyance,
    description: data.description,
    company_id: companyId,
  }

  if (!formattedData.id){
    showError("Data No Found")
    return
  }

  updateActivityLogWorkStatus(formattedData, data.id)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        emit('submitted')
      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
    loading.value = false
  })
}

const handleSave = () => {
    loading.value = true;
    let companyId = route.params.companyId;
    let query = '?company_id=' + companyId;
    const data = childRef.value.getFormData();

    const formattedData = {
      short_description: data.title,
      start_time: data.start_time ? formatDateTime(data.start_time) : null,
      end_time: data.end_time ? formatDateTime(data.end_time) : null,
      date: data.start_time ? formatToDate(data.start_time) : null,
      assigned_to: data.assign_to ? data.assign_to.id : null,
      report_to: data.report_to ? data.report_to.id : null,
      area_id: data.area_id ? data.area_id.id : null,
      participant_ids: data.participants ? data.participants.map(p => p.id) : [],
      activity_setup_id: data.activity_setup_id,
      tags: data.tag ? data.tag.map(t => t.id) : [],
      project_id: data.project ? data.project.id : null,
      project_activity_id: data.project_activity ? data.project_activity.id : null,
      full_description: data.description,
      type: data.type,
      client_id: data.client ? data.client.id : null,
      priority_id: data.priority ? data.priority.id : null,
      deal_id: data.deal ? data.deal.id : null,
      route_name: data.route_name,
      company_id: companyId
    };

    const formData = new FormData();
    Object.entries(formattedData).forEach(([key, value]) => {
      Array.isArray(value)
          ? value.forEach((item, i) => formData.append(`${key}[${i}]`, item))
          : formData.append(key, value !== null ? value : '');
    });

      const attachments = data.attachments;
      if (attachments && attachments.length) {
        handleAttachments(formData, attachments);
      }

   if (!data.id){
     storeTaskActivityLog(formData, query)
         .then(res => {
           if (!res.status) {
             showError(res.message)
             return
           }
           showSuccess(res.message)
           emit('submitted')
           closeModal()
         })
         .catch(err => {
           console.log(err)
         }).finally(() => {
       loading.value = false
     })
   }

   if (data.id){
     updateTaskActivityLog(formData, data.id)
         .then(res => {
           if (!res.status) {
             showError(res.message)
             return
           }
           showSuccess(res.message)
           emit('submitted')
           closeModal()
         })
         .catch(err => {
           console.log(err)
         }).finally(() => {
       loading.value = false
     })
   }
}

const handleAttachments = (formData, attachments) =>  {
  const previousFileIds = attachments
      .filter(attachment => typeof attachment === 'object' && attachment.hasOwnProperty('id'))
      .map(attachment => attachment.id);

  attachments.forEach((attachment, index) => {
    if (typeof attachment !== 'object' || !attachment.hasOwnProperty('id')) {
      formData.append(`attachments[${index}]`, attachment);
    }
  });

  if (previousFileIds.length) {
    formData.append('previous_file_ids', JSON.stringify(previousFileIds));
  }
}

onMounted(() => {
  let companyQuery = '?company_id=' + route.params.companyId;

  fetchTagRegisterList(companyQuery).then(res => {
    if (res.data) {
      tagList.value = res.data;
    }
    let noteMasterQuery = companyQuery + '&is_all=true&status=active&note_type=priority'
    fetchNoteMasterList(noteMasterQuery).then(res => {
      if (res.data) {
        priorityList.value = res.data
      }
    });
  })
  })

const closeModal = () => {
    store.state.isModalOpen = false;
    store.state.isModalOpenTwo = false;
    props.markAsDoneShow = false;
    props.isCompleted = false
    nextTick(() => {
      childRef.value && childRef.value.resetForm();
    });
    emit('submitted')
  }

</script>


<style scoped>
.new-customer {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
}
.med-title {
    padding: 0.486rem 1.5rem;
}
.add-note-modal {
    display: block; 
    background: #1c1d1c33
}
.filter-label {
    padding-right: 0.5rem !important;
}
.btn-margin {
    margin-right: 30px;
    margin-bottom: 10px;
}
.ml-2 {
    margin-left: 20px;
}
</style>